// A row's opened detail, under it: it grows into place and dissolves out.
// docs/features/shared-table.md, "renderSubRow".

import React, { useEffect, useRef } from 'react';
import { MS, usePresence } from '../ui/motion';

interface SubRowProps {
  colSpan: number;
  /** The consumer's node, or nothing for a closed row. */
  content: React.ReactNode;
  /** One step above the row it opens from. */
  tone: string;
  style?: React.CSSProperties;
  /** Reports whether the detail is still in the DOM, so its row keeps its bottom corners square until it is gone. */
  onPresent?: (present: boolean) => void;
  /** Lets the detail glide with its row when the rows move. */
  lineKey?: string;
}

const SubRow: React.FC<SubRowProps> = ({ colSpan, content, tone, style, onPresent, lineKey }) => {
  const show = content != null && content !== false;
  const { mounted, open } = usePresence(show, MS.exit);
  // What leaves is what was last shown.
  const last = useRef<React.ReactNode>(content);
  if (show) last.current = content;

  const present = show || mounted;
  useEffect(() => {
    onPresent?.(present);
  }, [present]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onPresent?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Rendered the moment it opens, before presence catches up: the content is in the tree at
  // once (a click's result is readable immediately) and only its height and opacity follow.
  if (!show && !mounted) return null;
  const grown = show && open;
  return (
    <tr aria-hidden={show ? undefined : true} data-line-key={lineKey}>
      <td colSpan={colSpan} className={`${tone} p-0`} style={style}>
        <div
          className={`grid transition-[grid-template-rows,opacity] motion-reduce:transition-[opacity] ${
            grown
              ? 'grid-rows-[1fr] opacity-100 duration-standard ease-emphasized'
              : 'grid-rows-[0fr] opacity-0 duration-exit ease-exit'
          }`}
        >
          <div className="min-h-0 overflow-hidden">{last.current}</div>
        </div>
      </td>
    </tr>
  );
};

export default SubRow;
